import React, { FC, useState } from 'react';
import { Button, Col, Input, Row, Tag, Typography } from 'antd';
import FileSaver from 'file-saver';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

import ClientVersionSearchSelect from './client-version-search-select';
import { useGetCurrentProgressContext } from './context';

const { Title } = Typography;

type Status = 'success' | 'no-result' | 'error';

export type ProgressFetcher = (params: {
  playerId: string;
  environment: string;
  clientVersion: string;
}) => Promise<{
  ok: boolean;
  data?: { data?: any } | null;
  error?: { message?: string } | null;
}>;

export type ProgressSearchProps = {
  fetchProgress: ProgressFetcher;
  downloadFileName: (playerId: string) => string;
};

const ProgressSearch: FC<ProgressSearchProps> = ({ fetchProgress, downloadFileName }) => {
  const { environment } = useGetCurrentProgressContext();
  const [playerId, setPlayerId] = useState<string>();
  const [clientVersion, setClientVersion] = useState<string>();
  const [data, setData] = useState<Record<string, any>>();
  const [status, setStatus] = useState<Status>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);

  const handleSearch = async () => {
    if (!playerId || !environment || !clientVersion) return;
    setIsFetching(true);
    setData(undefined);
    setStatus(undefined);
    setErrorMessage(undefined);
    const response = await fetchProgress({ playerId, environment, clientVersion }).finally(() => {
      setIsFetching(false);
    });
    if (response.ok) {
      if (response.data?.data) {
        setData(response.data.data);
        setStatus('success');
      } else {
        setStatus('no-result');
      }
    } else {
      setStatus('error');
      setErrorMessage(response.error?.message);
    }
  };

  const handleDownload = () => {
    if (!data || !playerId) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, downloadFileName(playerId));
  };

  return (
    <div className="flex flex-col">
      <Row gutter={[16, 16]} className="mb-4">
        <Col>
          <Title level={5} className="mr-2">
            Player ID
          </Title>
          <Input
            value={playerId}
            placeholder="Enter player ID"
            style={{ width: 250 }}
            onChange={(e) => setPlayerId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            Client Version
          </Title>
          <ClientVersionSearchSelect
            environment={environment}
            value={clientVersion}
            style={{ width: 200 }}
            onChange={setClientVersion}
            disabled={!environment}
          />
        </Col>
        <Col className="flex items-end">
          <Button
            htmlType="submit"
            type="primary"
            loading={isFetching}
            onClick={handleSearch}
            disabled={!playerId || !environment || !clientVersion}
          >
            <SearchOutlined />
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]} align="middle">
        <Col>
          {status === 'success' && <Tag color="green">Success</Tag>}
          {status === 'no-result' && <Tag color="orange">No Result</Tag>}
          {status === 'error' && <Tag color="red">{`Error : ${errorMessage}`}</Tag>}
        </Col>
        {status === 'success' && (
          <Col>
            <Button icon={<DownloadOutlined />} onClick={handleDownload}>
              Download
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default ProgressSearch;
